import axios from 'axios';
import { useEffect, useState } from 'react';
import { IInvoice } from '../../interfaces';
import { setGlobalState } from '../../state';
import { normalizePaymentProvider } from '../../functions/paymentProvider';
import {
    clearStripeReturnNote,
    hasStripeReturnNote,
    waitForPayment
} from '../../functions/stripeReturn';

/**
 * Waits for Stripe's webhook when the customer is back from Stripe's payment
 * page before it reached the backend. `confirming` stays true while waiting;
 * the paid invoice replaces the given one once it arrives.
 */
const useStripePaymentConfirmation = (invoice: IInvoice) => {
    const awaiting =
        normalizePaymentProvider(invoice.payment_provider) === 'stripe' &&
        !invoice.is_signed &&
        hasStripeReturnNote(invoice.uuid);
    const [confirming, setConfirming] = useState(awaiting);
    const [paidInvoice, setPaidInvoice] = useState<IInvoice | null>(null);

    useEffect(() => {
        if (!awaiting) return;
        const controller = new AbortController();
        const fetchInvoice = () =>
            axios
                .get<IInvoice>(
                    import.meta.env.VITE_API_BASE_URL + 'web/invoice/' + invoice.uuid + '/'
                )
                .then((res) => res.data);

        waitForPayment(fetchInvoice, { signal: controller.signal }).then((paid) => {
            if (controller.signal.aborted) return;
            clearStripeReturnNote(invoice.uuid);
            if (paid) {
                setGlobalState('invoice', paid);
                setPaidInvoice(paid);
            }
            setConfirming(false);
        });

        return () => controller.abort();
    }, [awaiting, invoice.uuid]);

    return { confirming, invoice: paidInvoice ?? invoice };
};

export default useStripePaymentConfirmation;
